import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Image from 'components/atoms/Image';
import Block from 'components/atoms/Block';
import Button, { LinkButton } from 'components/atoms/Button/Button';
import Container from 'components/atoms/Container/Container';
import Icon from 'components/atoms/Icon';
import { H1 } from 'components/atoms/Heading';
import { translate } from 'utils/translate';
import Toggle from 'components/utilities/Toggle';
import FocusLock from 'components/utilities/FocusLock';

import LazyLoad from 'react-lazyload';
import Modal from 'components/utilities/Modal';
import {
  chevronDown,
  chevronUp,
  chevronLeft,
  chevronRight,
  grid,
  plus,
} from 'components/atoms/Icon/icons';
const Slider = lazy(() =>
  import(
    /* webpackChunkName: "slider" */ /* webpackPreload: true */ 'packages/react-spring-slider/Slider'
  ),
);
const AutoPlay = lazy(() =>
  import(
    /* webpackChunkName: "slider" */ /* webpackPreload: true */ 'packages/react-spring-slider/AutoPlay'
  ),
);
const SlideTrack = lazy(() =>
  import(
    /* webpackChunkName: "slider" */ /* webpackPreload: true */ 'packages/react-spring-slider/SlideTrack'
  ),
);
const FadeTrack = lazy(() =>
  import(
    /* webpackChunkName: "slider" */ /* webpackPreload: true */ 'packages/react-spring-slider/FadeTrack'
  ),
);
const SliderState = lazy(() =>
  import(
    /* webpackChunkName: "slider" */ /* webpackPreload: true */ 'packages/react-spring-slider/SliderState'
  ),
);
import arrayChunk from 'utils/arrayChunk';
import { getVideoData } from 'utils/utils';
import VideoPlayer from 'components/utilities/VideoPlayer';
import * as styles from './ProductPresentationSlider.scss';
import LoadingIndicator from 'components/utilities/LoadingIndicator';
import { stripHtml } from 'utils/utils';

const isExternalVideo = item =>
  ['Youtube Video', 'Vimeo Video'].includes(item.externalLinkTypeName);

const ThumbNailNavigation = ({ onSelect, items = [], chunkCount = 3 }) => {
  const chunks = useMemo(
    () =>
      arrayChunk(
        items.map(item => ({ ...item })),
        chunkCount,
      ),
    [items],
  );
  return (
    <Suspense fallback={<LoadingIndicator />}>
      <Slider items={chunks}>
        <div className={styles.navContainer}>
          <div className={styles.nav}>
            <SlideTrack
              dragTreshold={60}
              direction="vertical"
              className={styles.navTrack}
              renderItem={({ item: chunk, getItemProps }) => (
                <div
                  className={styles.navItem}
                  {...getItemProps()}
                  key={`chunk_${chunk[0].url}`}
                >
                  {chunk.map(item => (
                    <LinkButton
                      className={styles.navItemButton}
                      onClick={() => onSelect(item)}
                      key={`item_${item.url}`}
                    >
                      <Image
                        className={classnames({
                          [styles.overlayPlayIcon]: item.type === 'video',
                        })}
                        src={item.thumbnail_url}
                        ratio={[3, 2]}
                        fit="cover"
                        cropDetails={item.cropDetails || []}
                      />
                    </LinkButton>
                  ))}
                </div>
              )}
            />
          </div>
          <SliderState>
            {({ isLast, isFirst, actions }) => (
              <React.Fragment>
                <LinkButton
                  className={classnames(styles.navButton, styles.prev)}
                  onClick={actions.prev}
                  disabled={isFirst}
                >
                  <Icon
                    icon={chevronUp}
                    role="presentation"
                    size={['1.4em', '1.4em']}
                  />
                </LinkButton>
                <LinkButton
                  className={classnames(styles.navButton, styles.next)}
                  onClick={actions.next}
                  disabled={isLast}
                >
                  <Icon
                    icon={chevronDown}
                    role="presentation"
                    size={['1.4em', '1.4em']}
                  />
                </LinkButton>
              </React.Fragment>
            )}
          </SliderState>
        </div>
      </Slider>
    </Suspense>
  );
};
ThumbNailNavigation.propTypes = {
  onSelect: PropTypes.func,
  items: PropTypes.array,
  chunkCount: PropTypes.number,
};

const SlideContent = ({ index, images, color }) => {
  const btnRef = React.useRef();

  const openModal = toggle => {
    toggle();
    setTimeout(() => btnRef.current.focus(), 200);
  };
  return (
    <Container
      className={classnames(styles.slideContentContainer, styles.inline)}
      noPadding
    >
      <Toggle
        on={false}
        render={({ on, toggle, setOff }) => (
          <React.Fragment>
            <div
              className={styles.slideContent}
              style={{ backgroundColor: `${color}33` }}
            >
              <p role='status' aria-live='polite' aria-atomic='true' aria-label='slider position' className={styles.title}>
                {index + 1} {translate('/product/of')} {images.length}
              </p>
              <LinkButton
                onClick={() => openModal(toggle)}
                className={styles.gallery}
              >
                {translate('/product/gallery')}
                <Icon icon={grid} />
              </LinkButton>
            </div>

            {on && (
              <FocusLock>
                <Modal
                  light
                  fullscreen
                  scrollLock
                  closeBtn={
                    <Button
                      ref={btnRef}
                      onClick={setOff}
                      className={styles.modalClose}
                    >
                      <Icon icon={plus} />
                    </Button>
                  }
                >
                  <Modal.Body className={styles.modal}>
                    <LazyLoad once height="100%">
                      <div className={styles.imageContainer}>
                        {images &&
                          images.length > 0 &&
                          images.map(item =>
                            item.type === 'video' ? (
                              <div key={item.url}>
                                <VideoPlayer
                                  url={item.url}
                                  ratio={[3, 2]}
                                  fit="contain"
                                />
                              </div>
                            ) : (
                              <Image
                                key={item.url}
                                src={item.url}
                                copy={item.copyright}
                                description={item.name}
                                ratio={[3, 2]}
                                fit="contain"
                                cropDetails={item.cropDetails || []}
                                lazy={false}
                              />
                            ),
                          )}
                      </div>
                    </LazyLoad>
                  </Modal.Body>
                </Modal>
              </FocusLock>
            )}
          </React.Fragment>
        )}
      />
    </Container>
  );
};
SlideContent.propTypes = {
  index: PropTypes.number,
  images: PropTypes.array,
  color: PropTypes.string,
};

const ProductPresentationSlider = React.memo(
  ({
    className,
    showThumbnailNavigation,
    product: { images = [], externalLinks = [] } = {},
    type = 'fade',
    color,
  }) => {
    const videos = useMemo(() => externalLinks.filter(isExternalVideo), [
      externalLinks,
    ]);
    const [videoData, setVideoData] = useState({});
    const [hasNavigated, setHasNavigated] = useState(false);
    const videoItems = useMemo(
      () =>
        videos.map((video, index) => ({
          ...video,
          ...videoData[video.url],
          type: 'video',
          index,
        })),
      [videos, videoData],
    );
    const imageItems = useMemo(
      () =>
        images.map((image, index) => ({
          ...image,
          type: 'image',
          thumbnail_url: image.url,
          index,
          id: image.url,
        })),
      [images],
    );
    const thumbnailItems = useMemo(() => [...videoItems, ...imageItems], [
      videoItems,
      imageItems,
    ]);
    const allItems = useMemo(
      () => [imageItems[0], ...videoItems, ...imageItems.slice(1)],
      [videoItems, imageItems],
    );
    const [selectedVideo, setSelectedVideo] = useState(null);
    useEffect(() => {
      // re-populate all video data
      if (videos && videos.length) {
        Promise.all(videos.map(video => getVideoData(video.url))).then(data =>
          setVideoData(
            data
              .filter(el => el != null)
              .reduce(
                (obj, item) => ({
                  ...obj,
                  [item.url]: item,
                }),
                {},
              ),
          ),
        );
      }
    }, [videos]);

    return type !== 'slide' ? (
      <Slider items={imageItems}>
        <div
          className={classnames(
            styles.root,
            className,
            showThumbnailNavigation && styles.withThumbnailNavigation,
          )}
        >
          {selectedVideo ? (
            <Block ratio={[3, 2]} className={styles.main}>
              <VideoPlayer
                url={selectedVideo.url}
                ratio={[16, 9]}
                thumbnailUrl={selectedVideo.thumbnail_url}
                disableLazy
              />
            </Block>
          ) : (
            <Block ratio={[3, 2]} className={styles.main}>
              {!hasNavigated && <AutoPlay interval={8000} />}
              <FadeTrack
                className={styles.mainTrack}
                renderItem={({ item, getItemProps }) => (
                  <div
                    {...getItemProps()}
                    className={classnames(styles.sliderItem, styles.mainItem)}
                  >
                    <Image
                      src={item.url}
                      copy={item.copyright}
                      description={item.name}
                      ratio={[3, 2]}
                      fit="cover"
                      cropDetails={item.cropDetails || []}
                    />
                  </div>
                )}
              />
            </Block>
          )}
          {thumbnailItems.length > 1 && showThumbnailNavigation && (
            <SliderState>
              {({ actions, index }) => (
                <ThumbNailNavigation
                  items={thumbnailItems}
                  selectedItem={
                    selectedVideo ||
                    thumbnailItems.find(
                      item => item.index === index && item.type === 'image',
                    )
                  }
                  onSelect={item => {
                    setHasNavigated(true);
                    if (item.type === 'video') {
                      setSelectedVideo(item);
                    } else {
                      setSelectedVideo(null);
                      actions.goTo(item.index);
                    }
                  }}
                />
              )}
            </SliderState>
          )}
        </div>
      </Slider>
    ) : (
      <Slider items={allItems}>
        <article
          className={classnames(styles.sliderRoot, styles.root, className)}
        >
          <SlideTrack
            direction="horizontal"
            swipe={allItems.length > 1}
            infinite={allItems.length > 1}
            snap="center"
            dragTreshold={100}
            height="item"
            renderItem={({ item, isCurrent, getItemProps }) => {
              return (
                <Container
                  {...getItemProps()}
                  noPadding
                  className={classnames(styles.largeSlide, {
                    [styles.currentItem]: isCurrent,
                  })}
                >
                  {item.type === 'video' ? (
                    <div>
                      <VideoPlayer
                        url={item.url}
                        ratio={[3, 2]}
                        fit="contain"
                      />
                    </div>
                  ) : (
                    <Block ratio={[3, 2]} contain className={styles.wrapper}>
                      <Image
                        src={item.url}
                        ratio={[3, 2]}
                        fit="contain"
                        copy={item.copyright}
                        description={item.name}
                        cropDetails={item.cropDetails || []}
                      />
                    </Block>
                  )}
                </Container>
              );
            }}
          />
          <SliderState>
            {({ items, index, actions }) => {
              return (
                <React.Fragment>
                  {items.length > 1 && (
                    <React.Fragment>
                      <LinkButton
                        className={classnames(styles.navButton, styles.prev)}
                        onClick={actions.prev}
                      >
                        <Icon icon={chevronLeft} />
                      </LinkButton>

                      <LinkButton
                        className={classnames(styles.navButton, styles.next)}
                        onClick={actions.next}
                      >
                        <Icon icon={chevronRight} />
                      </LinkButton>
                    </React.Fragment>
                  )}
                  <SlideContent color={color} index={index} images={items} />
                </React.Fragment>
              );
            }}
          </SliderState>
        </article>
      </Slider>
    );
  },
);
ProductPresentationSlider.displayName = 'ProductPresentationSlider';
ProductPresentationSlider.propTypes = {
  product: PropTypes.object,
  className: PropTypes.string,
  showThumbnailNavigation: PropTypes.bool,
  type: PropTypes.string,
  isFullWidthSlider: PropTypes.bool,
  color: PropTypes.string,
};
ProductPresentationSlider.defaultProps = {
  showThumbnailNavigation: false,
};

export default ProductPresentationSlider;
